import React, { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import SEO from "../components/SEO";
import { db } from "../firebase";
import "../styles/Contact.css";
import { useScrollReveal } from "../hooks/useScrollReveal";

const SUPPORT_EMAIL = "contact@macromatrix.io";

const faqItems = [
  {
    q: "How quickly do you respond to inquiries?",
    a: "Most inquiries receive an initial response within one business day.",
  },
  {
    q: "Do you work with startups and enterprises?",
    a: "Yes. We support both early-stage products and established business platforms.",
  },
  {
    q: "Can you take over an existing app?",
    a: "Yes. We run a technical audit first, then provide a practical transition plan.",
  },
];

const servicesList = [
  "Mobile Game Development",
  "App Development",
  "LiveOps & Analytics",
  "AR/VR Solutions",
  "Blockchain Gaming",
  "Consulting & Strategy",
  "Other (Please specify)"
];

function Contact() {
  useScrollReveal();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    budget: '',
    message: ''
  });
  const [honeypot, setHoneypot] = useState(''); // spam trap
  const [lastSubmit, setLastSubmit] = useState(0); // rate limit

  const [formStatus, setFormStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Spam: honeypot filled → silent drop
    if (honeypot) return;
    // Rate limit: 60s between submissions
    const now = Date.now();
    if (now - lastSubmit < 60_000) {
      setFormStatus('error');
      return;
    }
    setLastSubmit(now);
    setIsSubmitting(true);
    setFormStatus('');

    const bodyText = `
MacroMatrix – Contact Form
============================
Name:    ${formData.name}
Email:   ${formData.email}
Company: ${formData.company || 'Not provided'}
Service: ${formData.service}
Budget:  ${formData.budget || 'Not specified'}

Message:
${formData.message}

============================
Submitted: ${new Date().toLocaleString()}
    `.trim();

    try {
      // 1. Save to Firestore (contact_requests collection)
      await addDoc(collection(db, "contact_requests"), {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        service: formData.service,
        budget: formData.budget,
        message: formData.message,
        createdAt: serverTimestamp(),
        status: "open",
      });

      // 2. Write to mail collection → Firebase Trigger Email extension fires the email
      await addDoc(collection(db, "mail"), {
        to: SUPPORT_EMAIL,
        from: "MacroMatrix Contact <contact@macromatrix.io>",
        replyTo: formData.email,
        message: {
          subject: `[MacroMatrix Contact] ${formData.service || "Inquiry"} from ${formData.name}`,
          text: bodyText,
        },
      });

      setFormStatus('success');

      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        service: '',
        budget: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact">
      <SEO title="Contact" description="Get in touch with MacroMatrix for project inquiries, partnerships, or support. Offices in Lahore, Pakistan and Business Bay, Dubai." path="/contact" />
      <section className="contact-hero">
        <h2>Contact MacroMatrix</h2>
        <p>
          For project inquiries, partnerships, or support requests, reach out
          and our team will respond promptly.
        </p>
      </section>

      <section className="contact-form-section reveal">
        <h3>Send Us a Message</h3>
        <p className="form-subtitle">Fill out the form below and we'll get back to you within 24 hours</p>
        
        <form onSubmit={handleSubmit} className="contact-form">
          {/* Honeypot — hidden from real users, bots fill it */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={e => setHoneypot(e.target.value)}
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
          />
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="John Doe"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company">Company Name</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Your Company Ltd."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="service">Service Interested In *</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a service...</option>
                {servicesList.map((service, index) => (
                  <option key={index} value={service}>{service}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="budget">Budget Range</label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
              >
                <option value="">Select budget range...</option>
                <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                <option value="$100,000+">$100,000+</option>
              </select>
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="message">Project Details *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows="6"
              placeholder="Tell us about your project, goals, timeline, and any specific requirements..."
            ></textarea>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>

          {formStatus === 'success' && (
            <div className="form-message success">
              ✓ Thank you! Your message has been sent successfully. We'll contact you soon.
            </div>
          )}
          
          {formStatus === 'error' && (
            <div className="form-message error">
              ✗ Sorry, there was an error sending your message. Please try again or email us directly.
            </div>
          )}
        </form>
      </section>

      <section className="contact-grid reveal">
        <article className="contact-card">
          <h3>General Inquiries</h3>
          <p>Email: support@macromatrix.io</p>
        </article>

        <article className="contact-card">
          <h3>Office</h3>
          <p>Office No 4-A-I Commercial Area, PGEHS, Lahore, Pakistan</p>
          <p>7th Floor, Opal Tower, 701–705, BJK Blvd, Business Bay, Dubai – UAE</p>
          <p>Business Hours: Mon-Fri, 9:00 AM - 6:00 PM</p>
        </article>

        <article className="contact-card">
          <h3>Careers</h3>
          <p>Email: careers@macromatrix.io</p>
          <p>Hiring updates are posted on our Careers page.</p>
        </article>
      </section>

      <section className="contact-faq reveal">
        <h3>Frequently Asked Questions</h3>
        <div className="faq-list">
          {faqItems.map((item) => (
            <article key={item.q} className="faq-item">
              <h4>{item.q}</h4>
              <p>{item.a}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Contact;
